import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";
import { Draggable } from "gsap/src/Draggable";
import Lenis from "lenis";


gsap.registerPlugin(SplitText, ScrollTrigger, Draggable);

// ✅ Conditional Lenis initialization: Disable on Safari
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const lenis = isSafari ? null : new Lenis({
  duration: 1.85,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
  smoothTouch: false
});

// ✅ Export Lenis instance for global access
export { lenis };

// Animation frame loop to sync Lenis with GSAP
function raf(time) {
  if (lenis) lenis.raf(time);
  requestAnimationFrame(raf);

}

function initOverlayPinnedStack({
  selector = '.stackedgallery',
  slideSelector = '.stackedinner',
  holdThenMove = true,   // hold 100vh, then overlay during next 100vh
  enableSnap = false     // keep it ultra-light; set true if you want snapping per step
} = {}) {
  const wrap = document.querySelector(selector);
  if (!wrap) return;
  const slides = gsap.utils.toArray(`${selector} ${slideSelector}`);
  if (slides.length < 2) return;
  
  // Get all text elements
  const textElements = slides.map(slide => slide.querySelector('.stackedtext'));
  
  // Ensure pinned wrapper + absolute stacking for slides
  gsap.set(wrap, { position: 'relative', height: '100vh', overflow: 'hidden' });
  gsap.set(slides, {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100vh',
    objectFit: 'cover',
    willChange: 'transform',
    zIndex: (i) => i + 1
  });
  
  // Set initial state for text elements: invisible and slightly below
  gsap.set(textElements, {
    opacity: 0,
    y: 10, // subtle upward motion starting point
    willChange: 'transform, opacity'
  });
  
  // Set first slide text to be visible initially
  gsap.set(textElements[0], { opacity: 1, y: 0 });
  
  // All slides except the first wait just below the viewport
  gsap.set(slides.slice(1), { yPercent: 100 });
  
  const transitions = slides.length - 1;
  const unitsPerTransition = holdThenMove ? 2 : 1; // 1 unit = 100vh of scroll
  const totalUnits = transitions * unitsPerTransition;
  
  const tl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: {
      trigger: wrap,
      start: 'top top',
      end: () => `+=${totalUnits * window.innerHeight}`,
      pin: true,
      scrub: true,
      anticipatePin: 1,
      snap: enableSnap
        ? {
            snapTo: (v) => {
              const step = 1 / totalUnits; // snap every unit (hold + move)
              return Math.round(v / step) * step;
            },
            duration: 0.2,
            ease: 'power1.inOut'
          }
        : false,
      onUpdate: (self) => {
        // Calculate which slide should be active based on scroll progress
        const progress = self.progress;
        const currentSlideIndex = Math.floor(progress * transitions);
        const slideProgress = (progress * transitions) % 1;
        
        // Animate text for each slide based on its visibility
        textElements.forEach((textEl, index) => {
          if (!textEl) return;
          
          let textOpacity = 0;
          let textY = 30;
          
          // Current active slide
          if (index === currentSlideIndex) {
            // Fade out as we scroll away from this slide
            const fadeOutProgress = holdThenMove ? 
              Math.max(0, (slideProgress - 0.5) * 2) : // Start fading at 50% if holdThenMove
              slideProgress;
            textOpacity = 1 - fadeOutProgress;
            textY = fadeOutProgress * -30; // subtle upward motion as it fades
          } 
          // Next slide coming in
          else if (index === currentSlideIndex + 1) {
            // Fade in when 50% visible
            const fadeInProgress = holdThenMove ?
              Math.max(0, (slideProgress - 0.5) * 2) : // Start fading at 50% if holdThenMove
              slideProgress;
            textOpacity = fadeInProgress;
            textY = 30 * (1 - fadeInProgress); // move up as it fades in
          } 
          // Previous slides (already passed)
          else if (index < currentSlideIndex) {
            // Keep invisible
            textOpacity = 0;
            textY = -30;
          }
          // Future slides (not yet reached)
          else {
            // Keep invisible and below
            textOpacity = 0;
            textY = 30;
          }
          
          gsap.set(textEl, {
            opacity: textOpacity,
            y: textY
          });
        });
      }
      // markers: true
    }
  });
  
  // Build the sequence: optional HOLD(1 unit) then MOVE(1 unit) per slide
  const proxy = { v: 0 }; // invisible value to consume time for HOLD segments
  for (let i = 1; i < slides.length; i++) {
    if (holdThenMove) tl.to(proxy, { v: i, duration: 1 }); // HOLD: 100vh, no visual change
    tl.to(slides[i], { yPercent: 0, duration: 1 });        // MOVE: overlay next 100vh
  }
  
  // Refresh after assets load and on resize/orientation changes
  const refresh = () => ScrollTrigger.refresh();
  window.addEventListener('load', refresh);
  window.addEventListener('resize', refresh);
  window.addEventListener('orientationchange', () => setTimeout(refresh, 300));
}
requestAnimationFrame(raf);

// ✅ Fix: Ensure Lenis updates when the page loads and lazy images load
let resizeTimeout;

function refreshLenis() {
  clearTimeout(resizeTimeout); // Cancel previous calls
  resizeTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      if (lenis) lenis.resize(); // ✅ Recalculate dimensions smoothly
    });
  }, 3000); // Adjust delay if necessary
}

// ✅ Refresh Lenis when the page fully loads (not on Safari)
if (!isSafari) {
  window.addEventListener("load", refreshLenis);
}



function createParallaxEffect() {
  const containers = gsap.utils.toArray('.parallax-img');

  containers.forEach(container => {
    const img = container.querySelector('img');

    if (!img) {
      console.warn("No <img> found in", container);
      return;
    }

    const setupParallax = () => {
      requestAnimationFrame(() => {
        const containerHeight = container.offsetHeight;
        const parallaxOffset = containerHeight * 0.15;
        const requiredHeight = containerHeight + parallaxOffset;
        img.style.height = `${requiredHeight}px`;

        gsap.fromTo(img, {
          y: -parallaxOffset
        }, {
          y: 0,
          ease: "none",
          scrollTrigger: {
            trigger: container,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.5,
            invalidateOnRefresh: true
          }
        });
      });
    };

    // Load handling
    if (img.complete && img.naturalHeight !== 0) {
      setupParallax();
    } else {
      img.addEventListener('load', setupParallax);
    }

    // Optional: re-run on resize for responsiveness, debounced
    let resizeDebounce;
    window.addEventListener("resize", () => {
      clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(() => {
        ScrollTrigger.getAll().forEach(st => st.kill());
        setupParallax();
        ScrollTrigger.refresh(true);
      }, 250);
    });
  });
}



function pinSections() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  gsap.utils.toArray('.pinned').forEach(section => {
    const isPhoneDisabled = section.classList.contains('disable-phone');
    if (isTouchDevice && isPhoneDisabled) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        scrub: true
      }
    });
  });
}

function ParallaxContent() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (isTouchDevice) return;

  gsap.utils.toArray('.parallax-content').forEach(el => {
    const speed = el.dataset.speed ? parseFloat(el.dataset.speed) : -20; // default: up

    gsap.to(el, {
      yPercent: speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}


// ✅ NEW FUNCTION
// ✅ SEO/AT-safe text reveal (no container hiding, ARIA-friendly, PRM-aware)
function animateTextReveal() {
  const targets = document.querySelectorAll(".TextReveal");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  
  targets.forEach(target => {
    const delay = parseFloat(target.getAttribute("data-delay")) || 0;
    let hasAnimated = false;
    
    // Capture original text for accessibility and fallback
    const originalText = (target.textContent || "").trim();
    
    const cleanup = () => {
      if (target._split) {
        target._split.revert();
        target._split = null;
      }
      if (target._stID) {
        ScrollTrigger.getById(target._stID)?.kill();
        target._stID = null;
      }
    };
    
    const run = () => {
      cleanup();
      
      // If user prefers reduced motion, don't split/animate—just show the text
      if (reduceMotion) {
        target.style.visibility = "visible";
        target.style.opacity = "1";
        return;
      }
      
      // Check if element is a heading or other semantic element that shouldn't have role="text"
      const tagName = target.tagName.toLowerCase();
      const isHeading = /^h[1-6]$/.test(tagName);
      const isSemanticElement = ['p', 'span', 'div', 'section', 'article'].includes(tagName);
      
      // Only apply role="text" and aria-label to appropriate elements
      if (!isHeading && isSemanticElement) {
        target.setAttribute("role", "text");
        if (originalText) target.setAttribute("aria-label", originalText);
      }
      
      // Important: keep the container visible; animate children only
      gsap.set(target, { visibility: "visible", opacity: 1 });
      
      const split = new SplitText(target, { type: "words,chars" });
      target._split = split;
      
      // Hide chars for animation, but from AT perspective they are hidden anyway
      split.chars.forEach(ch => ch.setAttribute("aria-hidden", "true"));
      gsap.set(split.chars, { opacity: 0, y: 10 });
      
      // If already in view and previously animated, show immediately
      const rect = target.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight - 100;
      
      if (hasAnimated && isInView) {
        gsap.set(split.chars, { opacity: 1, y: 0 });
        return;
      }
      
      // Unique id for cleanup between refreshes
      const uniqueId = `textReveal-${Date.now()}-${Math.random()}`;
      const st = ScrollTrigger.create({
        id: uniqueId,
        trigger: target,
        start: "top bottom-=100",
        once: true,
        onEnter: () => {
          if (!target._split) return;
          gsap.to(target._split.chars, {
            duration: 0.065,
            opacity: 1,
            y: 0,
            ease: "circ.out",
            stagger: 0.01,
            delay,
            onComplete: () => { hasAnimated = true; }
          });
        }
      });
      
      target._stID = uniqueId;
    };
    
    run();
    
    // Rebuild on meaningful resizes
    let initialW = window.innerWidth, initialH = window.innerHeight, tId;
    const handleResize = () => {
      clearTimeout(tId);
      tId = setTimeout(() => {
        const wDelta = Math.abs(window.innerWidth - initialW);
        const hDelta = Math.abs(window.innerHeight - initialH);
        if (wDelta > 100 || hDelta > 100) {
          initialW = window.innerWidth;
          initialH = window.innerHeight;
          run();
        }
      }, 250);
    };
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", () => setTimeout(handleResize, 500));
  });
}


function setupOffCanvasMenu() {
  const menuToggle = document.querySelector('.burger');
  const menu = document.querySelector('.side-drawer');
  const overlay = document.querySelector('.overlay');
  const menuItems = document.querySelectorAll('.navbar-side li');
  const lowerContent = document.querySelector('.lower-content');
  const [line1, line2, line3] = ['line1', 'line2', 'line3'].map(id => document.getElementById(id));
  
  if (!menuToggle || !menu || !overlay || !line1 || !line2 || !line3) {
    console.warn('Off-canvas menu elements missing.');
    return;
  }
  
  const menuTimeline = gsap.timeline({
    paused: true,
    onStart: () => document.body.classList.add('menu-open'),
    onReverseComplete: () => document.body.classList.remove('menu-open')
  });
  
  menuTimeline
    .set(menu, { 
      position: 'fixed',
      top: 0,
      left: 0,
      y: '-100%',
      zIndex: 9999
    })
    .set(lowerContent, {
      opacity: 0,
      y: 20
    })
    .to(menu, { 
      y: '0%',
      duration: 1.2, 
      ease: 'power3.out' 
    }, 0)
    .to(overlay, { 
      opacity: 0.7,
      pointerEvents: 'auto', 
      duration: 0.4
    }, 0)
    .from(menuItems, {
      y: 20,
      opacity: 0,
      duration: 0.6,
      ease: 'power2.out'
    }, '-=0.4')
    .to(lowerContent, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: 'power2.out'
    }, '+=0.03');
  
  const burgerTimeline = gsap.timeline({ paused: true })
    .to(line1, { rotate: 45, y: 6, transformOrigin: 'center', duration: 0.25 }, 0)
    .to(line2, { opacity: 0, duration: 0.25 }, 0)
    .to(line3, { rotate: -45, y: -6, transformOrigin: 'center', duration: 0.25 }, 0);
  
  const toggle = () => {
    if (menuTimeline.progress() === 0 || menuTimeline.reversed()) {
      menuTimeline.play();
      burgerTimeline.play();
      menuToggle.setAttribute('aria-expanded', 'true');
    } else {
      menuTimeline.reverse();
      burgerTimeline.reverse();
      menuToggle.setAttribute('aria-expanded', 'false');
    }
  };
  
  const close = () => {
    menuTimeline.reverse();
    burgerTimeline.reverse();
    menuToggle.setAttribute('aria-expanded', 'false');
  };
  
  menuToggle.addEventListener('click', toggle);
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}



function setupModalCTA() {
  const ctaButton = document.querySelector('.fixedBottomCTA');
  const modal = document.querySelector('.cta-modal');
  
  if (!ctaButton || !modal) return;
  
  // Set initial state
  gsap.set(modal, { 
    opacity: 0, 
    visibility: 'hidden',
    pointerEvents: 'none',
    force3D: true 
  });
  
  // Add proper ARIA attributes for accessibility
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'modal-title');
  modal.setAttribute('aria-describedby', 'modal-description');
  
  // Set initial aria-expanded state
  ctaButton.setAttribute('aria-expanded', 'false');
  ctaButton.setAttribute('aria-controls', modal.id || 'cta-modal');
  
  // Track focus for accessibility
  let previouslyFocused = null;
  let draggableInstance = null;
  
  // Create draggable functionality
  const enableDragging = () => {
    if (typeof Draggable !== 'undefined') {
      const dragHandle = modal.querySelector('.modal-header, .drag-handle');
      
      if (dragHandle) {
        draggableInstance = Draggable.create(modal, {
          type: "x,y",
          trigger: dragHandle, // Only draggable by the handle element
          bounds: "body",
          inertia: true,
          cursor: "grab",
          activeCursor: "grabbing",
          edgeResistance: 0.65,
          onDragStart: function() {
            // Add visual feedback when dragging starts
            gsap.to(modal, { scale: 1.02, duration: 0.2 });
          },
          onDragEnd: function() {
            // Return to normal scale when drag ends
            gsap.to(modal, { scale: 1, duration: 0.3, ease: "back.out(1.2)" });
          }
        })[0];
        
        // Set cursor style on the handle
        dragHandle.style.cursor = 'move';
      }
    } else {
      console.warn('GSAP Draggable plugin not loaded - modal will not be draggable');
    }
  };
  
  // Lightweight open animation
  const open = () => {
    // Store currently focused element
    previouslyFocused = document.activeElement;
    
    document.body.style.overflow = 'hidden';
    ctaButton.setAttribute('aria-expanded', 'true');
    
    gsap.set(modal, { visibility: 'visible' });
    
    gsap.to(modal, {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1)',
      pointerEvents: 'auto',
      duration: 0.4,
      ease: 'back.out(1.2)',
      onComplete: () => {
        // Enable dragging after modal is fully open
        enableDragging();
        
        // Focus first focusable element in modal
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    });
  };
  
  // Lightweight close animation
  const close = () => {
    // Disable dragging before closing
    if (draggableInstance) {
      draggableInstance.kill();
      draggableInstance = null;
    }
    
    gsap.to(modal, {
      opacity: 0,
      transform: 'translate(-50%, -50%) scale(0.9)',
      pointerEvents: 'none',
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => {
        gsap.set(modal, { 
          visibility: 'hidden',
          // Reset position to center for next time
          x: 0,
          y: 0
        });
        document.body.style.overflow = '';
        ctaButton.setAttribute('aria-expanded', 'false');
        
        // Return focus to previously focused element
        if (previouslyFocused) {
          previouslyFocused.focus();
          previouslyFocused = null;
        }
      }
    });
  };
  
  // Event listeners
  ctaButton.addEventListener('click', (e) => {
    e.preventDefault();
    open();
  });
  
  // Close button functionality
  modal.addEventListener('click', (e) => {
    // Close if clicking close button
    if (e.target.classList.contains('modal-close') || e.target.closest('.modal-close')) {
      close();
    }
  });
  
  // Keyboard accessibility
  document.addEventListener('keydown', (e) => {
    if (modal.style.pointerEvents === 'auto') {
      // Close on Escape
      if (e.key === 'Escape') {
        close();
      }
      
      // Trap focus within modal
      if (e.key === 'Tab') {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  });
}



export default {
  init: function() {
    pinSections();
    ParallaxContent();
    createParallaxEffect();
    animateTextReveal();
    setupOffCanvasMenu();
    initOverlayPinnedStack();
    setupModalCTA();
  }
};